import useSesame from '@oberoninternal/travelbase-ds/hooks/useSesame';
import React, { FC, useState } from 'react';
import { MyOrderFragment, OrderItem_Ticket_Fragment } from '../../../generated/graphql';
import { applicable, not } from '../../../utils/fp';
import { isDeclined, isDone, isRecent, isReviewable } from '../../../utils/order';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import { FormattedMessage, useIntl } from 'react-intl';
import SectionTitleWrapper from './SectionTitleWrapper';
import EmptyState from './EmptyState';
import Anchor from '../../designsystem/Anchor';
import Link from 'next/link';
import { orderItemIsTicket } from '../../OrderTicket';
import ReviewTile from '../../ReviewTile';
import ReviewModal from '../../ReviewModal';

interface Props {
    data: MyOrderFragment[];
}

const ActivityReviews: FC<React.PropsWithChildren<Props>> = ({ data }) => {
    const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
    const { formatMessage } = useIntl();
    const [selectedTicketWithoutReviewId, setSelectedTicketWithoutReviewId] = useState<string | null>(null);

    const { onClose: closeReviewModal, open: reviewIsOpen, onOpen: openReviewModal } = useSesame();
    const { onClose: closeWithoutReviewModal, open: withoutreviewIsOpen, onOpen: openWithoutReviewModal } = useSesame();

    const orderItemsWithoutReview = data.reduce<OrderItem_Ticket_Fragment[]>(
        (curr, next) =>
            curr.concat(
                next.orderItems
                    .filter(orderItemIsTicket)
                    .filter(applicable(orderItemIsTicket, isDone, isReviewable, isRecent, not(isDeclined)))
            ),
        []
    );

    const orderItemsWithReview = data.reduce<OrderItem_Ticket_Fragment[]>(
        (curr, next) =>
            curr.concat(next.orderItems.filter(orderItemIsTicket).filter(item => item.activityReview !== null)),
        []
    );

    const selectedTicketWithoutReview =
        selectedTicketWithoutReviewId &&
        data
            .flatMap(order => order.orderItems)
            .find((item): item is OrderItem_Ticket_Fragment => item.id === selectedTicketWithoutReviewId);

    const selectedTicket =
        selectedTicketId &&
        data
            .flatMap(order => order.orderItems)
            .find((item): item is OrderItem_Ticket_Fragment => item.id === selectedTicketId);

    if (orderItemsWithReview.length === 0 && orderItemsWithoutReview.length === 0) {
        return (
            <EmptyState
                title={<FormattedMessage defaultMessage="Er zijn nog geen activiteiten om te beoordelen." />}
                description={
                    <FormattedMessage
                        defaultMessage="Laat na je bezoek aan een <activityLink>activiteit</activityLink> of <accoLink>accommodatie</accoLink> een beoordeling achter."
                        values={{
                            activityLink: children => (
                                <Link href="/agenda" legacyBehavior>
                                    <Anchor>{children}</Anchor>
                                </Link>
                            ),
                            accoLink: children => (
                                <Link href="/search" legacyBehavior>
                                    <Anchor>{children}</Anchor>
                                </Link>
                            ),
                        }}
                    />
                }
            />
        );
    }

    return (
        <>
            {orderItemsWithoutReview.length !== 0 && (
                <SectionTitleWrapper>
                    <Title elementType="h3">
                        <FormattedMessage defaultMessage="Activiteiten die wachten op jouw beoordelingen" />
                    </Title>
                </SectionTitleWrapper>
            )}
            {orderItemsWithoutReview.map((item, i) => (
                <ReviewTile
                    key={i}
                    title={
                        item.activity?.name ??
                        formatMessage({ defaultMessage: 'Deze activiteit is niet meer beschikbaar' })
                    }
                    thumbnail={item.activity?.jpegThumbnail!}
                    webThumbnail={item.activity?.webpThumbnail!}
                    arrivalDate={item.timeslot?.startDateTime ?? ''}
                    onLeaveReviewClick={() => {
                        setSelectedTicketWithoutReviewId(item.id ?? null);
                        openWithoutReviewModal();
                    }}
                    isUnavailable={!item.activity}
                />
            ))}
            {orderItemsWithReview.length !== 0 && (
                <SectionTitleWrapper>
                    <Title elementType="h3">
                        <FormattedMessage defaultMessage="Jouw beoordelingen" />
                    </Title>
                </SectionTitleWrapper>
            )}
            {orderItemsWithReview.map((item, i) => (
                <ReviewTile
                    key={i}
                    title={
                        item.activity?.name ??
                        formatMessage({ defaultMessage: 'Deze accommodatie is niet meer beschikbaar' })
                    }
                    isUnavailable={!item.activity}
                    thumbnail={item.activity?.jpegThumbnail!}
                    webThumbnail={item.activity?.webpThumbnail!}
                    arrivalDate={item.timeslot?.startDateTime ?? ''}
                    onReviewClick={() => {
                        setSelectedTicketId(item.id ?? null);
                        openReviewModal();
                    }}
                    review={item.activityReview!}
                />
            ))}
            {selectedTicketWithoutReview && (
                <ReviewModal
                    data={selectedTicketWithoutReview}
                    open={withoutreviewIsOpen}
                    onClose={closeWithoutReviewModal}
                />
            )}
            {selectedTicket && <ReviewModal data={selectedTicket} open={reviewIsOpen} onClose={closeReviewModal} />}
        </>
    );
};

export default ActivityReviews;
